import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
    Eye,
    Clock,
    Users,
    TrendingUp,
    Zap,
    DollarSign,
    ArrowRight,
} from 'lucide-react'

interface InitiativeCardProps {
    initiative: {
        id: string
        title: string
        short_description?: string
        description?: string
        domain?: string
        category?: string
        priority: string
        from_maturity_level?: number
        to_maturity_level?: number
        from_level?: number
        to_level?: number
        impact_level?: string
        impact?: string
        effort_level?: string
        effort?: string
        duration_weeks?: number
        team_size_min?: number
        team_size_max?: number
        team_size?: number
        budget_range_min?: number
        budget_range_max?: number
        budget_range?: string
        is_active?: boolean
    }
    onViewDetails: () => void
}

const PRIORITY_COLORS: Record<string, string> = {
    critical: 'bg-red-100 text-red-700 border-red-200',
    high: 'bg-orange-100 text-orange-700 border-orange-200',
    medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    low: 'bg-green-100 text-green-700 border-green-200',
}

const IMPACT_ICONS: Record<string, string> = {
    high: '🔴',
    medium: '🟡',
    low: '🟢',
    transformational: '🔴',
    significant: '🟠',
    moderate: '🟡',
    minimal: '🟢',
}

export function InitiativeCard({ initiative, onViewDetails }: InitiativeCardProps) {
    const impactLabel = initiative.impact_level || initiative.impact || ''
    const effortLabel = initiative.effort_level || initiative.effort || ''
    const domainLabel = initiative.domain || initiative.category || ''

    const hasBudgetRange = initiative.budget_range_min && initiative.budget_range_max
    const budgetText = hasBudgetRange
        ? `$${initiative.budget_range_min}k – $${initiative.budget_range_max}k`
        : initiative.budget_range || null

    const fromLevel = initiative.from_maturity_level ?? initiative.from_level
    const toLevel = initiative.to_maturity_level ?? initiative.to_level
    const hasLevelTransition = fromLevel != null && toLevel != null

    const teamText = initiative.team_size_min && initiative.team_size_max
        ? `${initiative.team_size_min}–${initiative.team_size_max} people`
        : initiative.team_size
            ? `${initiative.team_size} people`
            : null

    return (
        <Card className="h-full flex flex-col hover:shadow-md transition-shadow duration-200 border border-gray-200">
            <CardContent className="p-5 flex flex-col h-full">
                {/* Card Header */}
                <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0 mr-2">
                        <h4 className="font-semibold text-sm leading-tight line-clamp-2">{initiative.title}</h4>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {initiative.short_description || initiative.description || ''}
                        </p>
                    </div>
                </div>

                {/* Badges Row */}
                <div className="flex flex-wrap gap-1.5 mb-3">
                    {domainLabel && (
                        <Badge variant="outline" className="text-[10px] capitalize">{domainLabel}</Badge>
                    )}
                    <Badge className={`text-[10px] capitalize border ${PRIORITY_COLORS[initiative.priority] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                        {initiative.priority}
                    </Badge>
                    {hasLevelTransition && (
                        <Badge variant="secondary" className="text-[10px]">
                            L{fromLevel} <ArrowRight className="h-2.5 w-2.5 mx-0.5 inline" /> L{toLevel}
                        </Badge>
                    )}
                </div>

                {/* Info Grid */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs text-muted-foreground mb-4 flex-1">
                    {initiative.duration_weeks != null && (
                        <div className="flex items-center gap-1.5">
                            <Clock className="h-3 w-3 shrink-0" />
                            <span>{initiative.duration_weeks} weeks</span>
                        </div>
                    )}
                    {teamText && (
                        <div className="flex items-center gap-1.5">
                            <Users className="h-3 w-3 shrink-0" />
                            <span>{teamText}</span>
                        </div>
                    )}
                    {impactLabel && (
                        <div className="flex items-center gap-1.5">
                            <TrendingUp className="h-3 w-3 shrink-0" />
                            <span className="capitalize">{IMPACT_ICONS[impactLabel] || ''} {impactLabel}</span>
                        </div>
                    )}
                    {effortLabel && (
                        <div className="flex items-center gap-1.5">
                            <Zap className="h-3 w-3 shrink-0" />
                            <span className="capitalize">{effortLabel} effort</span>
                        </div>
                    )}
                    {budgetText && (
                        <div className="flex items-center gap-1.5 col-span-2">
                            <DollarSign className="h-3 w-3 shrink-0" />
                            <span>{budgetText}</span>
                        </div>
                    )}
                </div>

                {/* Card Actions — read-only, only View Details */}
                <div className="flex items-center pt-3 border-t border-gray-100 mt-auto">
                    <Button
                        variant="outline"
                        size="sm"
                        className="text-xs"
                        onClick={onViewDetails}
                    >
                        <Eye className="h-3 w-3 mr-1" />
                        View Details
                    </Button>
                </div>
            </CardContent>
        </Card>
    )
}
